import { useCallback, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Stack,
  IconButton,
  Chip,
  Popover,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import Iconify from '../../../../components/iconify';
import { useTimeline } from '../TimelineContext';

const MARKER_COLORS = [
  { value: '#1976d2', label: 'Blue' },
  { value: '#2e7d32', label: 'Green' },
  { value: '#d32f2f', label: 'Red' },
  { value: '#ed6c02', label: 'Orange' },
  { value: '#9c27b0', label: 'Purple' },
  { value: '#0288d1', label: 'Cyan' },
  { value: '#616161', label: 'Grey' },
];

const DEFAULT_COLOR = '#1976d2';

function formatTimestamp(ms) {
  return new Date(ms).toLocaleTimeString();
}

export default function NotesWidget({
  workspaceNotes = '',
  workspaceMarkers = [],
  tabNotes = '',
  tabMarkers = [],
  onUpdateNotes,
  onUpdateMarkers,
  onUpdateTabNotes,
  onUpdateTabMarkers,
  // Legacy prop aliases
  timelineMarkers,
  widgetConfig,
  onUpdateWidget,
}) {
  const { currentTime, seek } = useTimeline();
  const [scope, setScope] = useState(widgetConfig?.notesScope || 'workspace'); // 'workspace' | 'tab'
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
  const [colorPickerIndex, setColorPickerIndex] = useState(null);

  // Determine which data/handlers to use based on scope
  const isWorkspace = scope === 'workspace';
  const notes = isWorkspace ? workspaceNotes : tabNotes;
  const markers = isWorkspace ? workspaceMarkers || timelineMarkers || [] : tabMarkers;
  const handleNotesChange = isWorkspace ? onUpdateNotes : onUpdateTabNotes;
  const handleMarkersChange = isWorkspace ? onUpdateMarkers : onUpdateTabMarkers;

  const addMarker = useCallback(() => {
    if (!handleMarkersChange) return;
    const entry = { time: currentTime, label: formatTimestamp(currentTime), text: '', color: DEFAULT_COLOR };
    const updated = [...markers, entry].sort((a, b) => a.time - b.time);
    handleMarkersChange(updated);
  }, [currentTime, markers, handleMarkersChange]);

  const updateMarkerText = useCallback(
    (index, text) => {
      if (!handleMarkersChange) return;
      const updated = markers.map((t, i) => (i === index ? { ...t, text } : t));
      handleMarkersChange(updated);
    },
    [markers, handleMarkersChange]
  );

  const updateMarkerColor = useCallback(
    (index, color) => {
      if (!handleMarkersChange) return;
      const updated = markers.map((t, i) => (i === index ? { ...t, color } : t));
      handleMarkersChange(updated);
      setColorPickerAnchor(null);
      setColorPickerIndex(null);
    },
    [markers, handleMarkersChange]
  );

  const removeMarker = useCallback(
    (index) => {
      if (!handleMarkersChange) return;
      const updated = markers.filter((_, i) => i !== index);
      handleMarkersChange(updated);
    },
    [markers, handleMarkersChange]
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', px: 1, py: 1.5 }}>
      {/* Scope toggle */}
      <ToggleButtonGroup
        value={scope}
        exclusive
        onChange={(_, v) => {
          if (v) {
            setScope(v);
            onUpdateWidget?.({ notesScope: v });
          }
        }}
        size="small"
        sx={{ mb: 1, '& .MuiToggleButton-root': { py: 0.25, px: 1, fontSize: '0.8rem', textTransform: 'none' } }}
      >
        <ToggleButton value="workspace">Workspace</ToggleButton>
        <ToggleButton value="tab">Tab</ToggleButton>
      </ToggleButtonGroup>

      <TextField
        multiline
        minRows={3}
        maxRows={6}
        placeholder={`${isWorkspace ? 'Workspace' : 'Tab'} notes...`}
        value={notes}
        onChange={(e) => handleNotesChange && handleNotesChange(e.target.value)}
        variant="outlined"
        size="small"
        sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
          {isWorkspace ? 'Workspace' : 'Tab'} Markers
        </Typography>
        <IconButton size="small" onClick={addMarker} title="Tag current time">
          <Iconify icon="eva:pin-fill" width={16} />
        </IconButton>
      </Stack>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {markers.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
            Click the pin icon to tag the current timeline position
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {markers.map((ts, i) => (
              <Stack key={i} direction="row" spacing={0.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: ts.color || DEFAULT_COLOR,
                    flexShrink: 0,
                    cursor: 'pointer',
                    mt: 0.25,
                    border: '2px solid',
                    borderColor: 'background.paper',
                    boxShadow: 1,
                  }}
                  title="Change color"
                  onClick={(e) => {
                    setColorPickerAnchor(e.currentTarget);
                    setColorPickerIndex(i);
                  }}
                />
                <Chip
                  label={ts.label}
                  size="small"
                  onClick={() => seek(ts.time)}
                  sx={{
                    height: 20,
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    flexShrink: 0,
                    bgcolor: ts.color || DEFAULT_COLOR,
                    color: '#fff',
                    '&:hover': { opacity: 0.85 },
                  }}
                />
                <TextField
                  size="small"
                  variant="standard"
                  placeholder="Note..."
                  value={ts.text || ''}
                  onChange={(e) => updateMarkerText(i, e.target.value)}
                  sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0 } }}
                />
                <IconButton size="small" onClick={() => removeMarker(i)}>
                  <Iconify icon="eva:close-fill" width={16} />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>

      {/* Color picker popover */}
      <Popover
        open={colorPickerAnchor != null}
        anchorEl={colorPickerAnchor}
        onClose={() => {
          setColorPickerAnchor(null);
          setColorPickerIndex(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack direction="row" spacing={0.5} sx={{ p: 1 }}>
          {MARKER_COLORS.map((c) => (
            <Box
              key={c.value}
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: c.value,
                cursor: 'pointer',
                border: '2px solid',
                borderColor: markers[colorPickerIndex]?.color === c.value ? 'text.primary' : 'transparent',
                '&:hover': { transform: 'scale(1.2)' },
                transition: 'transform 0.1s',
              }}
              title={c.label}
              onClick={() => updateMarkerColor(colorPickerIndex, c.value)}
            />
          ))}
        </Stack>
      </Popover>
    </Box>
  );
}
